import React, { useState, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/core/close';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import Link from '@atlaskit/link';
import { Anchor, Box, Flex, Grid, Inline, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import type { TriggerAnalyticsEventType } from '@confluence/editor-features';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { getLogger } from '@confluence/logger';
import { useSessionData } from '@confluence/session-data';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { usePageContentId } from '@confluence/page-context';

import { EditorControlsFeedbackCollector } from './EditorControlsFeedbackCollector';
import type { BannerType } from './EditorControlsBannerDismissal';
import toolbarButtonImage from './assets/toolbar-button.svg';

const logger = getLogger('EditorControlsBanner');

const feedbackBannerStyles = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	color: 'color.text.subtle',
	padding: 'space.150',
	backgroundColor: 'color.background.accent.purple.subtlest',
	alignItems: 'center',
	justifyItems: 'end',
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
	textAlign: 'center',
	display: 'inline-block',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 'space.100',
	wordBreak: 'break-word',
});

const feedbackBannerContainerStyles = xcss({
	gridArea: 'message',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	wordBreak: 'break-word',
	flexWrap: 'wrap',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

const bannerHighlightedMessageStyles = xcss({
	display: 'none',
	[media.above.sm]: {
		display: 'inline-block',
	},
});

const i18n = defineMessages({
	bannerMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.message',
		defaultMessage:
			'Try the new formatting toolbar that appears when you select text. Insert elements with + on hover. <link>Learn more</link>.',
		description:
			'This is the message on the editor controls onboarding banner. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerHighlightedMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.highlighted-message',
		defaultMessage: '<strong>Looking for your toolbar?</strong>',
		description: 'This is the highlighted message on the editor controls onboarding banner.',
	},
	bannerMessageForClassicPages: {
		id: 'editor-action-bar.editor-controls-onboarding.message-classic',
		defaultMessage:
			'Keep your toolbar at the top or try the <link>new floating toolbar</link>. Give it a go, and share your <feedback>feedback</feedback>.',
		description:
			'This is the message on the editor controls onboarding banner for the classic pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForLivePages: {
		id: 'editor-action-bar.editor-controls-onboarding.message-live-pages',
		defaultMessage:
			'Try the <link>new formatting toolbar</link> that appears when you select text, and share your <feedback>feedback</feedback>.',
		description:
			'This is the message on the editor controls onboarding banner for the live pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerClose: {
		id: 'editor-action-bar.editor-controls-onboarding.close',
		defaultMessage: 'Close',
		description: 'clicking this icon closes the onboarding banner',
	},
	lozengeMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.lozenge',
		defaultMessage: 'New',
		description: 'This is the message on the lozenge in the editor controls onboarding banner',
	},
	collectorTitle: {
		id: 'editor-action-bar.feedback-collector.title',
		defaultMessage: 'Give feedback',
		description: 'title for the feedback collector',
	},
	collectorTitleDetails: {
		id: 'editor-action-bar.feedback-collector.title-details',
		defaultMessage:
			'Your thoughts are valuable and help us improve. {lineBreak}{lineBreak} If you’d like to chat with us further and stay informed about this Early Access Program, join our {communityGroupLink}.',
		description:
			"This message is seeking the user's input for improvements and invites them to join a specific group for more information and updates about the Early Access Program.",
	},
	collectorCommunityGroup: {
		id: 'editor-action-bar.feedback-collector.community-group',
		defaultMessage: 'Community group',
		description:
			'Clicking this message takes the user to the community group for the Early Access Program.',
	},
	imageAltText: {
		id: 'editor-action-bar.editor-controls-onboarding.image-alt-text',
		defaultMessage: 'New formatting toolbar button',
		description: 'This is the alt text for the image in the editor controls onboarding banner',
	},
	shareYourFeedback: {
		id: 'editor-action-bar.editor-controls-onboarding.share-your-feedback',
		defaultMessage: 'Share your <feedback>feedback</feedback>.',
		description: 'This is the message on the editor controls onboarding banner',
	},
	bannerMessageForTopToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-top-toolbar',
		defaultMessage:
			'Keep your toolbar at the top or try the <link>in-line toolbar</link>. It appears when you select text.',
		description:
			'This is the message on the editor controls onboarding banner for the classic pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForInlineToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-inline-toolbar',
		defaultMessage: 'Select text to open your <link>in-line toolbar</link>.',
		description:
			'This is the message on the editor controls onboarding banner for the classic pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForLivePagesInlineToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-live-pages-inline-toolbar',
		defaultMessage: 'Select text for your <link>in-line toolbar to appear</link>.',
		description:
			'This is the message on the editor controls onboarding banner for the live pages. It informs the user that the toolbar will be shown when content is selected',
	},
});

export const LEARN_MORE_LINK_URL_INTERNAL =
	'https://hello.atlassian.net/wiki/spaces/CONF/blog/2025/04/17/5174674433/The+new+modern+editing+experience+is+now+on+Hello';

export const LEARN_MORE_LINK_URL_EXTERNAL_LIVE_PAGES =
	'https://support.atlassian.com/confluence-cloud/docs/get-to-know-the-new-and-simplified-content-layout/';

export const LEARN_MORE_LINK_URL_EXTERNAL_CLASSIC_PAGES =
	'https://support.atlassian.com/confluence-cloud/docs/how-to-use-the-new-toolbar-and-insert-elements/';

export const LEARN_MORE_LINK_URL_EXTERNAL = LEARN_MORE_LINK_URL_EXTERNAL_CLASSIC_PAGES;

export const COMMUNITY_LINK_URL =
	'https://community.atlassian.com/forums/Confluence-articles/Live-Docs-Beta-update-Control-where-the-edit-tool-bar-lives/ba-p/3005144#M13863';

const CommunityGroupLink = () => (
	<Anchor href={COMMUNITY_LINK_URL} target="_blank">
		<FormattedMessage {...i18n.collectorCommunityGroup} />
	</Anchor>
);

const ATLASSIAN_ORG_ID = '2346a038-3c8c-498b-a79b-e7847859868d'; // using the atlassian org id to distinguish between internal and external orgs

export type EditorControlsOnboardingBannerProps = {
	onDismiss: () => void;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	bannerType: BannerType;
};

type DismissedStorageObject = {
	isDismissed: boolean;
};

// clean up these 5 functions below once the new dismissal logic is fully rolled out - platform_editor_controls_new_dimissal_logic_copy
const isValidStorageObject = (unknownObj: unknown): unknownObj is DismissedStorageObject => {
	return (
		typeof unknownObj === 'object' &&
		unknownObj !== null &&
		!Array.isArray(unknownObj) &&
		'isDismissed' in unknownObj &&
		typeof unknownObj.isDismissed === 'boolean'
	);
};

const parseStorageString = (storageString?: string): DismissedStorageObject | undefined => {
	if (!storageString) {
		return undefined;
	}

	try {
		const parsedObject = JSON.parse(storageString);

		if (isValidStorageObject(parsedObject)) {
			return parsedObject;
		}
	} catch (error) {
		logger.error`Error parsing editor controls onboarding banner dismissed storage: ${error}`;
	}
};

const getStorageKey = (isLivePage: boolean) => {
	return isLivePage
		? PERSISTED_KEYS_ON_SERVER.PERSISTED_EDITOR_CONTROLS_ONBOARDING_BANNER_LIVE_PAGE_DISMISSED
		: PERSISTED_KEYS_ON_SERVER.PERSISTED_EDITOR_CONTROLS_ONBOARDING_BANNER_CLASSIC_PAGE_DISMISSED;
};

export const getStorageObject = (isLivePage: boolean): DismissedStorageObject => {
	const key = getStorageKey(isLivePage);
	const dismissedStr = localStorage.getItem(key);

	return parseStorageString(dismissedStr) || { isDismissed: false };
};

const setStorageObject = (storageObject: DismissedStorageObject, isLivePage: boolean) => {
	const key = getStorageKey(isLivePage);
	localStorage.setItem(key, JSON.stringify(storageObject));
};

const getRevisedBannerMessage = (isLivePage: boolean, bannerType: BannerType) => {
	if (isLivePage && bannerType === 'toolbar-inline') {
		return i18n.bannerMessageForLivePagesInlineToolbar;
	}
	return bannerType === 'toolbar-top'
		? i18n.bannerMessageForTopToolbar
		: i18n.bannerMessageForInlineToolbar;
};

const getExistingBannerMessage = (isLivePage: boolean, bannerType: BannerType) => {
	if (fg('platform_editor_controls_new_dimissal_logic_copy')) {
		return bannerType === 'toolbar-top'
			? i18n.bannerMessageForClassicPages
			: i18n.bannerMessageForLivePages;
	}

	return isLivePage ? i18n.bannerMessageForLivePages : i18n.bannerMessageForClassicPages;
};

export const EditorControlsBanner = ({
	onDismiss,
	triggerAnalyticsEvent,
	isLivePage,
	isEmbeddedConfluence,
	bannerType,
}: EditorControlsOnboardingBannerProps) => {
	const { formatMessage } = useIntl();
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	const [contentId] = usePageContentId();
	const { orgId } = useSessionData();
	useEffect(() => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.displayed', {
				isLivePage,
			});
		}
	}, [triggerAnalyticsEvent, isLivePage]);

	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	const handleOnClickClose = () => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.dismissed', {
				isLivePage,
			});
		}
		if (!fg('platform_editor_controls_new_dimissal_logic_copy')) {
			setStorageObject({ isDismissed: true }, isLivePage);
		}
		onDismiss();
	};

	const handleOnClickFeedbackButton = () => {
		setShowFeedbackCollector(true);
	};

	const handleSubmitFeedback = () => {
		handleOnClickClose();
		setShowFeedbackCollector(false);
	};

	const isInternalUser = orgId === ATLASSIAN_ORG_ID;

	const getLearnMoreLinkUrl = () => {
		if (isInternalUser) {
			return LEARN_MORE_LINK_URL_INTERNAL;
		}

		if (fg('platform_editor_controls_banner_same_link')) {
			return LEARN_MORE_LINK_URL_EXTERNAL;
		} else {
			if (isLivePage) {
				return LEARN_MORE_LINK_URL_EXTERNAL_LIVE_PAGES;
			}
			return LEARN_MORE_LINK_URL_EXTERNAL_CLASSIC_PAGES;
		}
	};

	if (isEmbeddedConfluence && fg('platform_editor_controls_hide_banner_embedded_page')) {
		return null;
	}

	return (
		<div data-vc="editor-controls-onboarding-banner" {...ssrPlaceholderIdProp}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={[feedbackBannerStyles, feedbackBannerLayerStyles]}
				testId="editor-controls-onboarding-banner"
			>
				<Flex xcss={feedbackBannerContainerStyles}>
					<Image src={toolbarButtonImage} alt={formatMessage(i18n.imageAltText)} />
					{fg('platform_editor_controls_revise_banner_copy') ? (
						<Flex xcss={feedbackBannerMessageStyles}>
							<Lozenge appearance="new" isBold>
								{formatMessage(i18n.lozengeMessage)}
							</Lozenge>{' '}
							{isLivePage && bannerType === 'toolbar-inline' && (
								<FormattedMessage
									{...i18n.bannerHighlightedMessage}
									values={{
										strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
									}}
								/>
							)}{' '}
							<FormattedMessage
								{...getRevisedBannerMessage(isLivePage, bannerType)}
								values={{
									link: (chunks: React.ReactNode[]) => (
										<Anchor href={getLearnMoreLinkUrl()} target="_blank">
											{chunks}
										</Anchor>
									),
									strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
								}}
							/>{' '}
							<FormattedMessage
								{...i18n.shareYourFeedback}
								values={{
									feedback: (chunks: React.ReactNode[]) => (
										// eslint-disable-next-line jsx-a11y/anchor-is-valid
										<Link href="#" onClick={handleOnClickFeedbackButton}>
											{chunks}
										</Link>
									),
								}}
							/>
						</Flex>
					) : (
						<Flex xcss={feedbackBannerMessageStyles}>
							<Lozenge appearance="new" isBold>
								{formatMessage(i18n.lozengeMessage)}
							</Lozenge>{' '}
							{((bannerType === 'toolbar-inline' &&
								fg('platform_editor_controls_new_dimissal_logic_copy')) ||
								(isLivePage && !fg('platform_editor_controls_new_dimissal_logic_copy'))) && (
								<Inline xcss={bannerHighlightedMessageStyles}>
									<FormattedMessage
										{...i18n.bannerHighlightedMessage}
										values={{
											strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
										}}
									/>
								</Inline>
							)}{' '}
							<FormattedMessage
								{...getExistingBannerMessage(isLivePage, bannerType)}
								values={{
									link: (chunks: React.ReactNode[]) => (
										<Anchor href={getLearnMoreLinkUrl()} target="_blank">
											{chunks}
										</Anchor>
									),
									feedback: (chunks: React.ReactNode[]) => (
										// eslint-disable-next-line jsx-a11y/anchor-is-valid
										<Link href="#" onClick={handleOnClickFeedbackButton}>
											{chunks}
										</Link>
									),
								}}
							/>
						</Flex>
					)}
				</Flex>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={EditorCloseIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>
			{showFeedbackCollector && (
				<EditorControlsFeedbackCollector
					feedbackTitle={formatMessage(i18n.collectorTitle)}
					feedbackTitleDetails={
						formatMessage(i18n.collectorTitleDetails, {
							lineBreak: <br />,
							communityGroupLink: <CommunityGroupLink />,
						}) as React.ReactChild
					}
					onClose={() => setShowFeedbackCollector(false)}
					onSubmit={handleSubmitFeedback}
					source="editor-controls-feedback-banner"
					contentId={contentId}
					isLivePage={isLivePage}
					isInternalUser={isInternalUser}
				/>
			)}
		</div>
	);
};
